

import {
    HttpAttempt,
    XhrHandler,
    attachHttpFailure,
    safeErrorName,
    safeNumberOf,
    safeRequestHostname,
    safeResponseText,
    safeResponseUrl,
    stripQuery,
} from './httpdiagnostics';
import {
    TimerBases,
    elapsedMs,
    safeUrlHostname,
    safeVisibilityState,
    startTimer,
} from './safeglobals';

/**
 * Minimal response shape consumed by this client and its callers. Lets us back the
 * client with XMLHttpRequest while callers keep using `.ok`, `.status` and `.text`.
 */
export interface HttpResponse {
    ok: boolean;
    status: number;
    /**
     * The response body, read ONCE at settle time.
     *
     * Data, not a `text()` accessor over a live xhr. `responseText` is a getter on an
     * object page scripts routinely replace, so a lazy accessor meant the body could
     * differ between the moment we measured it - `bytes` and `bodyLooksHtml` in the
     * attempt log are derived from it - and the moment the caller parsed it. The value
     * taken at settle time IS the response; nothing read later can contradict it.
     *
     * '' when the body was unreadable, which surfaces downstream as a parse failure
     * (and so as 'truncated-body'/'intercepted'), not as a silently empty success.
     */
    text: string;
    /** Final URL after redirects, query-stripped. '' where unavailable. */
    url?: string;
    /** True when the response came from a different host than was requested. */
    redirected?: boolean;
    /**
     * The attempt log for this request, including any retries that preceded the
     * response.
     *
     * Present on COMPLETED responses (2xx and non-2xx alike) so a caller that
     * rejects an otherwise-successful response - a 404, an unparseable body, a
     * wrong-shaped config - can attach the real evidence to its own Error. Without
     * this the caller had to fabricate an attempt record, reporting status 0 and
     * losing both the true status and the bodyLooksHtml flag.
     */
    attempts?: HttpAttempt[];
}

/**
 * The outcome of one attempt. `response` is null when nothing completed (network
 * error, timeout, abort, or a synchronous throw from open()/send()).
 */
interface AttemptOutcome {
    response: HttpResponse | null;
    attempt: HttpAttempt;
}

export class HttpClient {

    /**
     * Fetch with retries, resolving with the response or throwing once retries are
     * exhausted. Signature unchanged; the diagnostics accumulator is seeded here
     * and kept off the public surface.
     */
    public static async fetchWithRetry(
        url: string,
        options: RequestInit = {},
        retries: number = 3,
        backoff: number = 1000,
        timeout: number = 8000
    ) : Promise<HttpResponse>
    {
        return HttpClient.fetchLoop(url, options, retries, backoff, timeout, [], 0);
    }

    /**
     * One pass of the retry loop, threading the attempt log through the recursion.
     *
     * Every settle path appends exactly one record, so the log that reaches Sentry
     * shows what each attempt actually did - which handler fired, the readyState it
     * reached, how long it took. Previously the catch here collapsed all of that
     * into the literal status `0`, which is why every failure looked identical.
     */
    protected static async fetchLoop(
        url: string,
        options: RequestInit,
        retries: number,
        backoff: number,
        timeout: number,
        attempts: HttpAttempt[],
        delayMs: number
    ) : Promise<HttpResponse>
    {

        // calculate delay for the next attempt
        const delay = backoff * 2 + Math.random() * 100;

        const outcome = await HttpClient.request(url, options, timeout, attempts.length + 1, delayMs);
        attempts.push(outcome.attempt);
        const response = outcome.response;

        if (response === null) {
            // nothing completed: network error, timeout, abort or a sync throw.
            // Describe what actually happened - reporting a CSP block or an abort as
            // a "network error" sends the next debugger down the wrong path.
            console.error(HttpClient.describeFailure(outcome.attempt.handler, url, timeout));
            // Some failures cannot succeed on a retry. Passing 0 retries hands this
            // straight to runRetry's terminal path, so the attempt log and the thrown
            // Error are assembled in exactly one place.
            const remainingRetries = HttpClient.canRetry(outcome.attempt.handler) ? retries : 0;
            return HttpClient.runRetry(delay, 0, url, options, remainingRetries, backoff, timeout, attempts);
        }

        // Hand the attempt log to the caller on every COMPLETED response. A 2xx whose
        // body turns out to be unparseable or wrong-shaped is still a failure, and the
        // caller needs this evidence (real status, bodyLooksHtml) to classify it.
        response.attempts = attempts;

        if (response.ok) {
            return response;
        }

        const cannotRetry = [404, 401];
        if (cannotRetry.indexOf(response.status) !== -1) {
            // certain http statuses will not change with a retry so return them
            return response;
        }
        // bad response but able to retry
        return HttpClient.runRetry(delay, response.status, url, options, retries, backoff, timeout, attempts);

    }

    /**
     * Whether a failed attempt is worth repeating.
     *
     * The counterpart to the `cannotRetry` status list below, and the same reasoning:
     * some failures will not change with a retry, so retrying only spends the backoff
     * to reproduce them.
     *
     * A synchronous throw from `open()`/`send()` never reached the network, and every
     * cause is a stable property of the page - a CSP `connect-src` SecurityError, a
     * malformed URL, an absent `XMLHttpRequest`, a forbidden header name. At the
     * production defaults, retrying one spent ~6.2s of pure backoff to produce four
     * identical SecurityErrors; with two configs fetched in sequence that was ~12.3s
     * before a single hidden field on the page was populated. The config that arrived
     * after the wait was the same fallback available immediately.
     *
     * `error` and `timeout` DO retry: a connection reset or a slow host can genuinely
     * succeed on a second attempt. `abort` is left retryable on purpose - it means the
     * page is navigating away, so the delay costs nobody anything and there is no win
     * to bank.
     */
    protected static canRetry(handler: XhrHandler) : boolean
    {
        return handler !== 'throw';
    }

    /** Console text matching what actually failed, for the developer at the console. */
    protected static describeFailure(handler: XhrHandler, url: string, timeout: number) : string
    {
        const prefix = 'fountain-analytics.HttpClient: ';
        if (handler === 'timeout') {
            return `${prefix}Request timed out after ${timeout}ms: ${url}`;
        }
        if (handler === 'abort') {
            return `${prefix}Request aborted (page unloading?): ${url}`;
        }
        if (handler === 'throw') {
            return `${prefix}Request blocked before it was sent (CSP or malformed URL): ${url}`;
        }
        return `${prefix}Network error fetching ${url}`;
    }

    /**
     * Apply request headers, handling every shape fetch's RequestInit allows:
     * a Headers instance, an array of [name, value] tuples, or a plain record.
     * (The Headers global is absent on old browsers, so it is typeof-guarded.)
     * Must be called after xhr.open() and before xhr.send().
     */
    protected static applyHeaders(xhr: XMLHttpRequest, headers?: HeadersInit) : void
    {
        if (!headers) {
            return;
        }
        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
            headers.forEach((value, name) => xhr.setRequestHeader(name, value));
            return;
        }
        if (Array.isArray(headers)) {
            for (let i = 0; i < headers.length; i ++) {
                const pair = headers[i];
                if (pair && pair.length === 2) {
                    xhr.setRequestHeader(pair[0], pair[1]);
                }
            }
            return;
        }
        const record = headers as Record<string, string>;
        for (const name in record) {
            if (Object.prototype.hasOwnProperty.call(record, name)) {
                xhr.setRequestHeader(name, record[name]);
            }
        }
    }

    /**
     * Perform a single request via XMLHttpRequest.
     *
     * XHR is used over fetch() for two reasons that matter on old browsers and
     * flaky connections: it has a native request `timeout` (fetch has none), and
     * on expiry it actually aborts the request rather than abandoning it.
     *
     * This NEVER rejects - it always resolves with a record of what happened. That
     * is the point: the onerror-vs-ontimeout distinction (the one signal that
     * separates "the host is unreachable or slow" from "this network is broken")
     * cannot be lost, because there is no catch left to flatten it away.
     */
    protected static request(
        url: string,
        options: RequestInit = {},
        timeout: number = 8000,
        n: number = 1,
        delayMs: number = 0
    ) : Promise<AttemptOutcome>
    {
        // One arrow, one line. `resolve` exists only inside the Promise executor, so
        // capturing it is forced by the language; everything else is an
        // AttemptSettler method over instance fields (invariant #10). This used to be
        // a 120-line executor holding five nested closures, which is where the one
        // unguarded read that could hang this promise forever went unnoticed.
        return new Promise<AttemptOutcome>(
            resolve => HttpClient.send(resolve, url, options, timeout, n, delayMs)
        );
    }

    /**
     * Open and send one XHR, routing every outcome to `settler`.
     *
     * Never throws: a synchronous failure from open()/send() - in the wild a CSP
     * connect-src SecurityError, or an InvalidStateError on a malformed URL - is
     * recorded as its own settle path rather than escaping.
     */
    protected static send(
        resolve: (outcome: AttemptOutcome) => void,
        url: string,
        options: RequestInit,
        timeout: number,
        n: number,
        delayMs: number
    ) : void
    {
        const settler = new AttemptSettler(
            resolve,
            // startTimer, not two independent "now" reads: if `performance` is stubbed
            // mid-request the two would come from different clock epochs and the
            // difference would be nonsense (~55,000 years). See safeglobals.
            startTimer(),
            safeVisibilityState(),
            // Resolved: the one-argument URL constructor throws on a relative config
            // URL, which left requestHost empty and silently disabled redirect
            // detection for every client using one.
            safeRequestHostname(url),
            n,
            delayMs,
        );
        try {
            const xhr = new XMLHttpRequest();
            settler.setXhr(xhr);
            xhr.open((options.method || 'GET').toString().toUpperCase(), url, true);

            // native, browser-enforced timeout (no Promise.race / AbortController needed)
            if (timeout > 0) {
                xhr.timeout = timeout;
            }

            // map fetch's credentials mode onto XHR. 'include' sends credentials on
            // cross-origin requests; anything else keeps XHR's default (same-origin
            // cookies only). Must be set after open() and before send().
            if (options.credentials === 'include') {
                xhr.withCredentials = true;
            }

            HttpClient.applyHeaders(xhr, options.headers);

            // .bind, not arrows: no closure, and each handler is a named method that
            // can be read (and tested) on its own.
            xhr.onload = settler.onLoad.bind(settler);
            xhr.onerror = settler.onError.bind(settler);
            xhr.ontimeout = settler.onTimeout.bind(settler);
            xhr.onabort = settler.onAbort.bind(settler);

            xhr.send((options.body as XMLHttpRequestBodyInit) || null);
        } catch (error) {
            // Log the error object itself: it is the only place the real message and
            // stack exist, and swallowing it would leave a developer with a
            // misleading "network error" in the console for a CSP block.
            console.error(error);
            settler.onThrow(error);
        }
    }

    protected static async runRetry(
        delay: number,
        lastResponseStatus: number,
        url: string,
        options: RequestInit = {},
        retries: number = 3,
        backoff: number = 1000,
        timeout: number = 8000,
        attempts: HttpAttempt[] = []
    ) : Promise<HttpResponse>
    {
        // if we run out of retries, throw an error
        if (retries <= 0) {
            // Query-stripped: this message becomes a Sentry issue title, and a config
            // URL can carry personal data in its query string.
            const safeUrl = stripQuery(url);
            const error = new Error(
                `fountain-analytics.HttpClient: Failed to Fetch ${safeUrl}: Status=${lastResponseStatus}`
            );
            // Carry the attempt log out on the Error itself. Attached as a property
            // rather than via an Error subclass, which breaks `instanceof` under an
            // es5 target - see attachHttpFailure.
            attachHttpFailure(error, { url: safeUrl, attempts: attempts });
            throw error;
        }

        console.warn(`Retrying in ${delay}ms... (${retries} attempts left)`);

        await new Promise(resolve => setTimeout(resolve, delay));

        return HttpClient.fetchLoop(url, options, retries - 1, backoff, timeout, attempts, delay);
    }

}

/**
 * Records what one HTTP attempt did and resolves its promise exactly once.
 *
 * Per-attempt mutable state (`settled`, the xhr) lives in instance fields rather
 * than in a closure over the Promise executor - invariant #10. That is not only
 * style: the previous closure form put the response construction inline in
 * `xhr.onload`, where an unguarded `xhr.status` read could throw, escape the handler,
 * and leave `settled` false with no reject path and no timeout able to fire. The
 * promise then never settled and initialisation hung silently.
 *
 * Every read of the xhr goes through a guarded helper, because a browser hands these
 * values to us once and page scripts routinely replace the object they come from.
 */
class AttemptSettler {

    private settled = false;
    private xhr: XMLHttpRequest | null = null;

    constructor(
        private readonly resolve: (outcome: AttemptOutcome) => void,
        private readonly bases: TimerBases,
        private readonly visStart: string,
        private readonly requestHost: string,
        private readonly n: number,
        private readonly delayMs: number,
    ) {}

    /** Called as soon as the xhr exists, so a later settle can read it. */
    public setXhr(xhr: XMLHttpRequest) : void {
        this.xhr = xhr;
    }

    /** A response completed - 2xx or not. */
    public onLoad() : void {
        this.settle('load', true);
    }

    public onError() : void {
        this.settle('error', false);
    }

    public onTimeout() : void {
        this.settle('timeout', false);
    }

    public onAbort() : void {
        this.settle('abort', false);
    }

    /** open()/send() threw synchronously - usually a CSP SecurityError. */
    public onThrow(error: any) : void {
        this.settle('throw', false, safeErrorName(error));
    }

    /**
     * Snapshot the xhr once and resolve.
     *
     * Every read of the xhr in this class happens here, exactly once per attempt.
     * That is deliberate on two counts: these values are gone the moment the handler
     * returns, and each one is a getter on an object page scripts routinely replace -
     * so reading any of them twice risks the attempt log and the response describing
     * different responses. One read, one snapshot, both consumers fed from it.
     *
     * @param completed whether a response actually came back, i.e. whether the caller
     *                  gets an HttpResponse or null.
     */
    private settle(handler: XhrHandler, completed: boolean, thrownName: string = '') : void {
        // A browser may fire more than one settle event; record only the first.
        if (this.settled) {
            return;
        }
        this.settled = true;
        const status = safeNumberOf(this.xhr, 'status');
        const readyState = safeNumberOf(this.xhr, 'readyState');
        const responseUrl = safeResponseUrl(this.xhr);
        // null means the body was unreadable, which is distinct from an empty body:
        // it is why `bytes` reports -1 rather than 0.
        const body = safeResponseText(this.xhr);
        this.resolve({
            response: completed ? this.buildResponse(status, body, responseUrl) : null,
            attempt: {
                n: this.n,
                handler: handler,
                status: status,
                readyState: readyState,
                responseURL: responseUrl,
                // -1 means "could not measure", NOT "instant" - the classifier must
                // not read it as a locally-refused request.
                ms: elapsedMs(this.bases),
                delayMs: this.delayMs,
                bytes: body === null ? -1 : body.length,
                // Only this boolean leaves the page, never the body itself. It is
                // enough to spot an HTML error page or captive-portal login served
                // where JSON was expected. Tested with a regex rather than a trim so
                // a large body is not copied on every successful fetch.
                bodyLooksHtml: body !== null && /^\s*</.test(body),
                // Name only (SecurityError vs InvalidStateError) - never the message,
                // which could carry a URL.
                thrownName: thrownName,
                visStart: this.visStart,
                visEnd: safeVisibilityState(),
            },
        });
    }

    private buildResponse(status: number, body: string | null, responseUrl: string) : HttpResponse {
        const finalHost = safeUrlHostname(responseUrl);
        return {
            ok: status >= 200 && status < 300,
            status: status,
            // An unreadable body becomes '', which fails to parse downstream and so
            // falls back to cache - rather than being handed over as an empty success.
            text: body === null ? '' : body,
            url: responseUrl,
            // Compare HOSTS, and only when both are known. Comparing full URLs
            // flagged same-host path redirects, and a relative request URL (legal for
            // XHR) has no host at all - which previously made every single response
            // report redirected: true.
            redirected: !!finalHost && !!this.requestHost && finalHost !== this.requestHost,
        };
    }

}
